import { CloudUpload, Download } from "@mui/icons-material";
import { Box, Button, FormGroup, FormGroupProps, FormLabel, styled } from "@mui/material";
import { FC, useEffect, useMemo, useState } from "react";
import { getExampleCollaboratorMassiveXlsxForDownload } from "src/apis/assets";
import { massiveCollaboratorFileUpload } from "src/apis/company";
import CompanySelect from "src/pages/components/CompanySelect";
import FormSaveAndResetButtons from "src/pages/components/FormSaveAndResetButtons";
import PageContainer from "src/pages/components/PageContainer";
import { getUserCompanyId, isUserAdministrator, isUserCompany } from "src/utils/auth";
import { downloadBlob } from "src/utils/blob";
import { Id } from "src/utils/types";

const CenterFormGroup: FC<FormGroupProps> = ({children}): JSX.Element => {
  return (
    <FormGroup
      sx={{
        textAlign: "center",
        display: "flex",
        alignContent: "center",
        alignItems: "center",
      }}
    >
      {children}
    </FormGroup>
  )
}

const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
});

const RegisterCollaboratorMassive: FC = (): JSX.Element => {
  const [error, setError] = useState<string>("");
  const [success, setSuccess] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [uploadFile, setUploadFile] = useState<File|undefined>();
  const [companyId, setCompanyId] = useState<Id>("");

  const isAdmin = useMemo<boolean>(isUserAdministrator, []);
  const isCompany = useMemo<boolean>(() => isUserCompany() && !isAdmin, [isAdmin]);
  const isSubmitDisabled = useMemo<boolean>(() => !Boolean(uploadFile) || !companyId, [uploadFile, companyId]);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    setLoading(true);

    try {
      const payload = new FormData();
      payload.append('file', uploadFile!);

      await massiveCollaboratorFileUpload(companyId, payload);
      setError("");
      setSuccess(true);
    } catch (err: any) {
      setError(err?.error)
      setSuccess(false)
    } finally {
      setLoading(false);
    }
  }

  const downloadExample = async () => {
    setLoading(true);

    try {
      const blob = await getExampleCollaboratorMassiveXlsxForDownload();

      downloadBlob(blob, "Modelo Cadastro Colaboradores Eezycare One.xlsx")
      setError("");
    } catch (err: any) {
      setError(err?.error);
      setSuccess(false);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (isCompany) {
      setCompanyId(getUserCompanyId())
    }
  }, [isCompany])

  return (
    <PageContainer
      title="Importar Colaboradores"
      error={error}
      guard={() => {
        return isUserAdministrator() || isUserCompany();
      }}
    >
      <form
        onSubmit={handleSubmit}
        style={{ display: "flex", flexDirection: "column", gap: "1rem" }}
      >
        {isAdmin && (
          <CompanySelect
            companyId={companyId}
            setCompanyId={setCompanyId}
            setError={setError}
            disabled={!isAdmin}
          />
        )}

        {Boolean(companyId) && (
          <>
            <CenterFormGroup>
              <FormLabel>
                Para começar a importação de colaboradores primeiro baixe o modelo de arquivo
              </FormLabel>
              <Button
                startIcon={<Download />}
                onClick={downloadExample}
                variant={"contained"}
                sx={{
                  marginTop: 1,
                }}
              >
                Baixar Modelo
              </Button>
            </CenterFormGroup>

            <CenterFormGroup>
              <FormLabel>
                <p>
                  Agora carregue o arquivo modelo preenchido com os dados de seus colaboradores.
                </p>
                É crucial que o nome das colunas e a extensão do arquivo não sejam alteradas, senão poderão ocorrer erros de processamento.
              </FormLabel>

              <Button
                component="label"
                role={undefined}
                variant="contained"
                tabIndex={-1}
                startIcon={<CloudUpload />}
                sx={{
                  marginTop: 1,
                }}
              >
                Carregar Arquivo

                <VisuallyHiddenInput
                  type="file"
                  onChange={(e) => {
                    const file = e.target.files;
                    if (file == null) {
                      setError("Pelo menos um arquivo deve ser selecionado, por favor tente novamente")
                      return
                    }

                    setUploadFile(file[0]);
                  }}
                />
              </Button>
            </CenterFormGroup>

            {!isSubmitDisabled && (
              <Box
                sx={{
                  width: "100%",
                  display: "flex",
                  placeContent: "center",
                  alignContent: "center",
                  marginTop: "1rem",
                }}
              >
                <FormSaveAndResetButtons
                  title={`Importar ${uploadFile?.name}`}
                  loadingTitle={`Enviando ${uploadFile?.name}`}
                  success={success}
                  loading={loading}
                  isSubmitDisabled={!Boolean(uploadFile) && !companyId}
                  resetFormState={() => {}}
                  isResetFormStateDisabled={true}
                />
              </Box>
            )}
          </>
        )}
      </form>
    </PageContainer>
  );
}

export default RegisterCollaboratorMassive;
